import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

// Audio context for sound effects
let audioContext: AudioContext | null = null;
let oscillator: OscillatorNode | null = null;
let gainNode: GainNode | null = null;

// Keyframe animations
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const rotate = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const scan = keyframes`
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(99, 102, 241, 0.5);
  }
  50% {
    box-shadow: 0 0 40px rgba(99, 102, 241, 0.8), 0 0 60px rgba(6, 182, 212, 0.6);
  }
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled components
const LoaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '300px',
  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)',
  borderRadius: '20px',
  border: '1px solid rgba(99, 102, 241, 0.2)',
  backdropFilter: 'blur(10px)',
  position: 'relative',
  overflow: 'hidden',
  animation: `${glow} 2s ease-in-out infinite`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '2px',
    background: 'linear-gradient(90deg, transparent, #6366f1, transparent)',
    animation: `${scan} 2s linear infinite`,
  },
}));

const OrbitalRing = styled(Box)({
  position: 'relative',
  width: '120px',
  height: '120px',
  marginBottom: '20px',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '100px',
    height: '100px',
    border: '2px solid rgba(99, 102, 241, 0.3)',
    borderRadius: '50%',
    transform: 'translate(-50%, -50%)',
    animation: `${rotate} 3s linear infinite`,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '80px',
    height: '80px',
    border: '2px solid rgba(6, 182, 212, 0.3)',
    borderRadius: '50%',
    transform: 'translate(-50%, -50%)',
    animation: `${rotate} 2s linear infinite reverse`,
  },
});

const CentralOrb = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: '20px',
  height: '20px',
  background: 'radial-gradient(circle, #6366f1 0%, #06b6d4 100%)',
  borderRadius: '50%',
  transform: 'translate(-50%, -50%)',
  animation: `${pulse} 1.5s ease-in-out infinite`,
  boxShadow: '0 0 20px rgba(99, 102, 241, 0.6)',
});

const LoadingText = styled(Typography)(({ theme }) => ({
  color: '#6366f1',
  fontSize: '1.2rem',
  fontWeight: 600,
  textAlign: 'center',
  marginBottom: '10px',
  textShadow: '0 0 10px rgba(99, 102, 241, 0.5)',
  animation: `${fadeIn} 0.5s ease-out`,
}));

const ProgressText = styled(Typography)(({ theme }) => ({
  color: '#06b6d4',
  fontSize: '0.9rem',
  textAlign: 'center',
  opacity: 0.8,
  animation: `${fadeIn} 0.5s ease-out 0.2s both`,
}));

const ProgressBar = styled(Box)(({ theme }) => ({
  width: '200px',
  height: '4px',
  background: 'rgba(99, 102, 241, 0.2)',
  borderRadius: '2px',
  marginTop: '15px',
  overflow: 'hidden',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    background: 'linear-gradient(90deg, #6366f1, #06b6d4)',
    borderRadius: '2px',
    animation: `${scan} 1.5s ease-in-out infinite`,
  },
}));

interface FuturisticLoaderProps {
  message?: string;
  showProgress?: boolean;
  progress?: number;
  size?: 'small' | 'medium' | 'large';
  enableSound?: boolean;
}

export const FuturisticLoader: React.FC<FuturisticLoaderProps> = ({
  message = 'Initializing System',
  showProgress = false,
  progress = 0,
  size = 'medium',
  enableSound = true,
}) => {
  const [currentMessage, setCurrentMessage] = useState(message);
  const [dots, setDots] = useState('');
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio context
  useEffect(() => {
    if (enableSound && typeof window !== 'undefined') {
      try {
        audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        gainNode = audioContext.createGain();
        gainNode.connect(audioContext.destination);
        gainNode.gain.value = 0.1; // Low volume
      } catch (error) {
        console.log('Audio context not supported');
      }
    }
  }, [enableSound]);

  // Animated dots
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // Sound effect for loading
  const playLoadingSound = () => {
    if (enableSound && audioContext && gainNode) {
      try {
        oscillator = audioContext.createOscillator();
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(440, audioContext.currentTime); // A4 note
        oscillator.connect(gainNode);
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.1);
      } catch (error) {
        console.log('Sound playback failed');
      }
    }
  };

  // Play sound periodically
  useEffect(() => {
    if (enableSound) {
      const soundInterval = setInterval(playLoadingSound, 2000);
      return () => clearInterval(soundInterval);
    }
  }, [enableSound]);

  // Dynamic messages
  const messages = [
    'Initializing System',
    'Loading Modules',
    'Establishing Connection',
    'Processing Data',
    'Calibrating Sensors',
    'System Ready',
  ];

  useEffect(() => {
    const messageInterval = setInterval(() => {
      setCurrentMessage(prev => {
        const currentIndex = messages.indexOf(prev);
        const nextIndex = (currentIndex + 1) % messages.length;
        return messages[nextIndex];
      });
    }, 3000);

    return () => clearInterval(messageInterval);
  }, []);

  const sizeConfig = {
    small: { width: '200px', height: '200px' },
    medium: { width: '300px', height: '300px' },
    large: { width: '400px', height: '400px' },
  };

  return (
    <LoaderContainer sx={sizeConfig[size]}>
      <OrbitalRing>
        <CentralOrb />
      </OrbitalRing>
      
      <LoadingText variant="h6">
        {currentMessage}{dots}
      </LoadingText>
      
      {showProgress && (
        <>
          <ProgressText variant="body2">
            {progress}% Complete
          </ProgressText>
          <ProgressBar />
        </>
      )}
      
      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: '#6366f1',
              animation: `${pulse} 1s ease-in-out infinite`,
              animationDelay: `${i * 0.2}s`,
            }}
          />
        ))}
      </Box>
    </LoaderContainer>
  );
};

export default FuturisticLoader; 